import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Form from '@shared/atoms/Form';
import FormInput from '@shared/atoms/FormFields/FormInput';
import Snackbar from '@shared/atoms/Snackbar';
import Dialog from '@shared/molecules/Dialog';
import { useForm } from '@shared/utils';
import theme from '@shared/themes/default';
import { courseStatus } from '@shared/const';

import { useAppGlobals } from '../../../contexts/AppContext';
import { createCourse } from '../graphql/mutations';
import { listCourses } from '../graphql/queries';

const CreateCourseDialog = ({ open, onClose, onCreate }) => {
  const [hasError, setHasError] = useState(false);
  const { locale, useGraphQLMutation } = useAppGlobals();

  const { t } = useTranslation();

  const [createCourseMutation] = useGraphQLMutation(createCourse, {
    update(cache, { data: { createCourse } }) {
      const variables = {
        filter: {
          status: [
            courseStatus.DRAFT,
            courseStatus.PUBLISHED,
            courseStatus.HIDDEN,
          ],
        },
      };
      const currentData = cache.readQuery({
        query: listCourses,
        variables,
      });
      if (currentData) {
        cache.writeQuery({
          query: listCourses,
          variables,
          data: {
            listCourses: [...currentData.listCourses, createCourse],
          },
        });
      }
    },
    onCompleted({ createCourse }) {
      onCreate(createCourse.id);
    },
    onError() {
      setHasError(true);
    },
  });

  const onSubmitHandler = (values) => {
    createCourseMutation({
      variables: {
        input: {
          name: [
            {
              languageCode: locale,
              value: values.courseName,
            },
          ],
        },
      },
    });
  };

  const {
    disableSubmit,
    errors,
    handleSubmit,
    handleBlur,
    handleChange,
    properties,
    submitting,
    values,
  } = useForm({
    fields: {
      courseName: {
        default: '',
        disableTrim: true,
        required: true,
        type: 'text',
        maxLength: 70,
      },
    },
    onSubmitHook: onSubmitHandler,
  });

  const actions = [
    {
      label: t('generic.cancel'),
      ariaLabel: t('generic.cancel'),
      type: 'secondaryOutline',
      handler: onClose,
    },
    {
      label: t('backoffice.courses.createDraft'),
      ariaLabel: t('backoffice.courses.createDraft'),
      handler: handleSubmit,
      disabled: disableSubmit,
    },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        actions={actions}
        title={t('backoffice.courses.createCourse')}
        ariaLabel={t('backoffice.courses.createCourse')}
        contentPadding={theme.spacing.m1}
        loading={submitting}
      >
        <Form id="create-course-form" onSubmit={handleSubmit}>
          <FormInput
            id="courseName"
            name="courseName"
            placeholder={t('backoffice.courses.courseTitlePlaceholder')}
            label={t('backoffice.courses.courseTitle')}
            errors={errors}
            handleBlur={handleBlur}
            handleChange={handleChange}
            properties={properties}
            showMaxLength
            theme="light"
            values={values}
            required
          />
        </Form>
      </Dialog>
      <Snackbar
        type="error"
        message={t('backoffice.courses.creationFailed')}
        open={hasError}
        onClose={() => setHasError(false)}
      />
    </>
  );
};

CreateCourseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
};

export default CreateCourseDialog;
